import { styled } from "@monolith-forensics/monolith-ui";
import { Link } from "react-router-dom";
import { File } from "../files.types";
import { useState } from "react";
import { useCaseFiles } from "../Hooks";
import { ManagedRowIcon } from "@/Features/EvidenceFiles/ManagedRowIcon";
import {
  canDropInto,
  isManagedFolder,
} from "@/Features/EvidenceFiles/evidence-files.utils";
import { isInternalDrag } from "../utils/dragAndDrop";

const StyledContainer = styled.div`
  &.data-cell {
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;

    .label {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      max-width: 100%;
    }
  }

  &.data-cell:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.palette.primary.main};
    cursor: pointer;
  }

  &.drop-target {
    font-weight: bold;
    background-color: ${({ theme }) => theme.palette.primary.main};
    border-radius: 4px;
    transition: background-color 0.2s ease;

    // disable pointer events for children
    & > * {
      pointer-events: none;
    }
  }
`;

const FOLDER_ICON_SRC = `${process.env.PUBLIC_URL}/static/icons/default_folder.svg`;

export const FolderComponent = ({ data }: { data: File }) => {
  const { handleItemDrop } = useCaseFiles();

  const [isDragging, setIsDragging] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);
  // The Evidence Files folder and item folders stay where Monolith put them
  // (ENG-2133).
  const managed = isManagedFolder(data);

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.setData("text/plain", JSON.stringify(data));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    // Check if the dragged item is a file/folder (internal) or files from file system (external)
    const hasFiles = e.dataTransfer.types.includes("Files");
    const hasTextData = e.dataTransfer.types.includes("text/plain");

    if (hasFiles || hasTextData) {
      // If the user is dragging an internal item, don't allow the drop onto itself
      if (isDragging && hasTextData) {
        setIsDropTarget(false);
        return;
      }
      if (!canDropInto(data, { internal: isInternalDrag(e) })) {
        setIsDropTarget(false);
        return;
      }
      setIsDropTarget(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    // A refused drop shows the not allowed cursor and never fires `drop`.
    e.dataTransfer.dropEffect = canDropInto(data, {
      internal: isInternalDrag(e),
    })
      ? "move"
      : "none";
  };

  const handleDragLeave = () => {
    setIsDropTarget(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation(); // prevent the drop from being handled by the table
    setIsDropTarget(false);

    // If the user is dragging an internal item, don't allow the drop onto itself
    if (isDragging) {
      return;
    }

    if (!canDropInto(data, { internal: isInternalDrag(e) })) return;

    handleItemDrop(e, data);
  };

  return (
    <Link
      to={`/cases/${data?.case?.case_id}/documents/folders/${data?.uuid}`}
      style={{ flex: 1 }}
      draggable={!managed}
      onDragStart={managed ? undefined : handleDragStart}
      onDragEnd={managed ? undefined : handleDragEnd}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <StyledContainer
        className={`data-cell ${isDropTarget ? "drop-target" : ""}`}
        title={data?.name}
        style={{
          zIndex: isDragging ? 1000 : "auto",
        }}
      >
        {managed ? (
          <ManagedRowIcon row={data} src={FOLDER_ICON_SRC} />
        ) : (
          <img src={FOLDER_ICON_SRC} alt="icon" style={{ height: "1.5rem" }} />
        )}
        <div className="label">{data?.name}</div>
      </StyledContainer>
    </Link>
  );
};
